import { useEffect, useRef, useState } from 'react';
import { Card, ErrorMessage, Field, Loading, ModuleHeader, Toggle, useAsync, useToast } from '../../components/ui';
import { IconFile } from '../../components/icons';
import { api, request } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

/**
 * Gestaltung der erzeugten Dokumente (Kap. 17).
 *
 * Die Maske beantwortet eine einzige Frage: Wie sieht das aus, was der Kunde
 * bekommt? Deshalb steht die Vorschau nicht am Rand, sondern in der Mitte –
 * jede Einstellung lässt sich sofort am Musterdokument prüfen, ohne dass
 * dafür ein echter Auftrag abgeschlossen werden müsste.
 */

const LEER = {
  brandColor: '#1f7a68',
  logoHeight: 34,
  footerNote: '',
  accentBar: true,
  letterheadActive: true,
  suppressOwnHeader: true,
  topMargin: 48,
  sendToContractor: true,
  copyRecipients: '',
  conditions: '',
};

type Form = typeof LEER;

const kilobyte = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

export function DocumentDesign() {
  const { notify } = useToast();
  const design = useAsync(() => api.get('/api/documents/design'), []);
  const [form, setForm] = useState<Form>(LEER);
  const [busy, setBusy] = useState<string | null>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const logoEingabe = useRef<HTMLInputElement>(null);
  const bogenEingabe = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const s = design.data?.settings;
    if (!s) return;
    setForm({
      brandColor: s['document.brandColor'] ?? LEER.brandColor,
      logoHeight: Number(s['document.logoHeight'] ?? LEER.logoHeight),
      footerNote: s['document.footerNote'] ?? '',
      accentBar: s['document.accentBar'] ?? !design.data?.assets?.letterhead,
      letterheadActive: s['document.letterheadActive'] ?? true,
      suppressOwnHeader: s['document.suppressOwnHeader'] ?? true,
      topMargin: Number(s['document.topMargin'] ?? LEER.topMargin),
      sendToContractor: s['contracting.sendToContractor'] ?? true,
      copyRecipients: s['contracting.copyRecipients'] ?? '',
      conditions: s['contracting.conditions'] ?? '',
    });
  }, [design.data]);

  // Die Vorschau ist ein Blob-Verweis; ohne Freigabe hielte jede erzeugte
  // Fassung ihren Speicher bis zum Neuladen der Seite.
  useEffect(() => () => { if (vorschau) URL.revokeObjectURL(vorschau); }, [vorschau]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((alt) => ({ ...alt, [key]: value }));

  const speichern = async () => {
    setBusy('speichern');
    try {
      await api.put('/api/documents/design', form);
      notify('Gestaltung gespeichert.', 'success');
      design.reload();
      await vorschauLaden();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern nicht möglich.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const vorschauLaden = async () => {
    const antwort = await request<Response>('/api/documents/preview', { raw: true });
    if (!antwort.ok) {
      notify('Die Vorschau konnte nicht erzeugt werden.', 'error');
      return;
    }
    const blob = await antwort.blob();
    setVorschau((alt) => {
      if (alt) URL.revokeObjectURL(alt);
      return URL.createObjectURL(blob);
    });
  };

  const hochladen = async (kind: 'logo' | 'letterhead', datei: File) => {
    setBusy(kind);
    try {
      const dataUrl = await new Promise<string>((auf, ab) => {
        const leser = new FileReader();
        leser.onload = () => auf(String(leser.result));
        leser.onerror = () => ab(new Error('Die Datei konnte nicht gelesen werden.'));
        leser.readAsDataURL(datei);
      });
      const antwort: any = await api.put(`/api/documents/assets/${kind}`, { filename: datei.name, dataUrl });
      notify(`${kind === 'logo' ? 'Logo' : 'Briefbogen'} hinterlegt: ${antwort.asset?.note ?? ''}`, 'success');
      design.reload();
      await vorschauLaden();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Hochladen nicht möglich.', 'error');
    } finally {
      setBusy(null);
      if (logoEingabe.current) logoEingabe.current.value = '';
      if (bogenEingabe.current) bogenEingabe.current.value = '';
    }
  };

  const entfernen = async (kind: 'logo' | 'letterhead') => {
    if (!window.confirm(`${kind === 'logo' ? 'Logo' : 'Briefbogen'} wirklich entfernen?`)) return;
    setBusy(kind);
    try {
      await api.delete(`/api/documents/assets/${kind}`);
      notify('Entfernt.', 'success');
      design.reload();
      await vorschauLaden();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Entfernen nicht möglich.', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (design.loading && !design.data) return <Loading />;
  if (design.error) return <ErrorMessage error={design.error} />;

  const logo = design.data?.assets?.logo;
  const bogen = design.data?.assets?.letterhead;
  const maxMb = ((design.data?.maxUploadBytes ?? 6291456) / 1024 / 1024).toFixed(0);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconFile width={22} height={22} />}
        title="Dokumentengenerator"
        subtitle="Wie erzeugte Dokumente aussehen – Briefbogen, Farbe und Kopfzeile lassen sich sofort an der Vorschau prüfen."
        pill="Konfiguration"
        tone="accent"
      />

      <Card
        title="Briefbogen"
        subtitle="Ihre PDF-Vorlage liegt unter jeder erzeugten Seite"
        actions={
          bogen && (
            <button className="btn ghost sm" disabled={busy === 'letterhead'} onClick={() => entfernen('letterhead')}>
              Entfernen
            </button>
          )
        }
      >
        <div className="stack" style={{ gap: 12 }}>
          <p className="small subtle" style={{ margin: 0 }}>
            Laden Sie Ihren Geschäftsbogen als <strong>PDF</strong> hoch. Die erste Seite wird unter jede Seite der
            erzeugten Dokumente gelegt – Auftragsbericht wie Beauftragung. Aus Word heraus: <em>Speichern unter →
            PDF</em>. Ein Word-Dokument selbst lässt sich nicht verwenden, es müsste erst gesetzt werden.
          </p>

          {bogen ? (
            <div className="note-box tone-success">
              <div className="row"  style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{bogen.filename}</div>
                  <div className="small subtle">
                    {bogen.note} · {kilobyte(bogen.size_bytes)} · geändert {formatDateTime(bogen.updated_at)}
                  </div>
                </div>
                <a
                  className="btn sm ghost"
                  href="#"
                  onClick={async (event) => {
                    event.preventDefault();
                    const antwort = await request<Response>('/api/documents/assets/letterhead/file', { raw: true });
                    window.open(URL.createObjectURL(await antwort.blob()), '_blank');
                  }}
                >
                  Ansehen
                </a>
              </div>
            </div>
          ) : (
            <div className="note-box tone-info">Kein Briefbogen hinterlegt – die Dokumente tragen den eingebauten Kopf.</div>
          )}

          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <input
              ref={bogenEingabe}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(event) => {
                const datei = event.target.files?.[0];
                if (datei) void hochladen('letterhead', datei);
              }}
            />
            <button className="btn" disabled={busy === 'letterhead'} onClick={() => bogenEingabe.current?.click()}>
              {busy === 'letterhead' ? 'Wird geprüft …' : bogen ? 'Andere PDF wählen' : 'PDF wählen …'}
            </button>
            <span className="small subtle" style={{ alignSelf: 'center' }}>höchstens {maxMb} MB</span>
          </div>

          {bogen && (
            <div className="stack" style={{ gap: 8 }}>
              <Toggle
                checked={form.letterheadActive}
                onChange={(v) => set('letterheadActive', v)}
                label="Briefbogen verwenden"
              />
              <Toggle
                checked={form.suppressOwnHeader}
                onChange={(v) => set('suppressOwnHeader', v)}
                label="Eigenen Kopf unterdrücken (Firmenname und Anschrift stehen schon auf dem Bogen)"
              />
              <Field
                label="Oberer Rand"
                hint="Wie viel Platz Ihr Briefbogen oben braucht, in Punkt. 48 ist der Standard; bei einem hohen Briefkopf eher 120–160."
              >
                <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                  <input
                    type="range"
                    min={0}
                    max={300}
                    step={4}
                    value={form.topMargin}
                    onChange={(event) => set('topMargin', Number(event.target.value))}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    min={0}
                    max={400}
                    value={form.topMargin}
                    onChange={(event) => set('topMargin', Number(event.target.value))}
                    style={{ width: 90 }}
                  />
                </div>
              </Field>
            </div>
          )}
        </div>
      </Card>

      <Card title="Logo und Farbe" subtitle="Gilt, wenn kein Briefbogen die Gestaltung vorgibt">
        <div className="form-grid">
          <Field label="Hausfarbe" hint="Überschriften, Linien und der Streifen am oberen Rand">
            <div className="row" style={{ gap: 8 }}>
              <input
                type="color"
                value={form.brandColor}
                onChange={(event) => set('brandColor', event.target.value)}
                style={{ width: 52, padding: 3, height: 38 }}
              />
              <input
                value={form.brandColor}
                onChange={(event) => set('brandColor', event.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </Field>

          <Field label="Logohöhe" hint="in Punkt, zwischen 12 und 90">
            <input
              type="number"
              min={12}
              max={90}
              value={form.logoHeight}
              onChange={(event) => set('logoHeight', Number(event.target.value))}
            />
          </Field>
        </div>

        <div className="stack" style={{ gap: 10, marginTop: 12 }}>
          {logo ? (
            <div className="note-box tone-success">
              <div className="row"  style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{logo.filename}</div>
                  <div className="small subtle">
                    {logo.note} · {kilobyte(logo.size_bytes)}
                  </div>
                </div>
                <button className="btn sm ghost" disabled={busy === 'logo'} onClick={() => entfernen('logo')}>
                  Entfernen
                </button>
              </div>
            </div>
          ) : (
            <div className="note-box tone-info">
              Kein Logo hinterlegt – im Kopf steht der Firmenname als Text. Das Logo muss eine{' '}
              <strong>JPEG-Datei</strong> sein; PNG und SVG lassen sich nicht einbetten.
            </div>
          )}

          <div className="row" style={{ gap: 10 }}>
            <input
              ref={logoEingabe}
              type="file"
              accept="image/jpeg,.jpg,.jpeg"
              style={{ display: 'none' }}
              onChange={(event) => {
                const datei = event.target.files?.[0];
                if (datei) void hochladen('logo', datei);
              }}
            />
            <button className="btn" disabled={busy === 'logo'} onClick={() => logoEingabe.current?.click()}>
              {busy === 'logo' ? 'Wird geprüft …' : logo ? 'Anderes Logo wählen' : 'JPG wählen …'}
            </button>
          </div>

          <Toggle
            checked={form.accentBar}
            onChange={(v) => set('accentBar', v)}
            label="Farbstreifen am oberen Seitenrand"
          />

          <Field label="Fußzeile" hint="Steht auf jeder Seite ganz unten – etwa Handelsregister und Bankverbindung.">
            <input
              value={form.footerNote}
              onChange={(event) => set('footerNote', event.target.value)}
              placeholder="HRB 12345 Hannover · USt-IdNr. DE123456789"
            />
          </Field>
        </div>
      </Card>

      <Card title="Beauftragung" subtitle="Versand an Dienstleister und Text auf dem Schreiben">
        <div className="stack" style={{ gap: 12 }}>
          <Toggle
            checked={form.sendToContractor}
            onChange={(v) => set('sendToContractor', v)}
            label="Beauftragung nach der Freigabe automatisch an den Dienstleister senden"
          />
          <Field
            label="Kopie an"
            hint="Weitere Empfänger, durch Komma getrennt – etwa die eigene Buchhaltung."
          >
            <input
              value={form.copyRecipients}
              onChange={(event) => set('copyRecipients', event.target.value)}
              placeholder="buchhaltung@example.de, leitung@example.de"
            />
          </Field>
          <Field
            label="Bedingungen"
            hint="Steht auf jedem Beauftragungsschreiben – Zahlungsziel, Rechnungsanschrift, Baustellenordnung."
          >
            <textarea
              value={form.conditions}
              onChange={(event) => set('conditions', event.target.value)}
              rows={5}
              placeholder="Zahlungsziel 30 Tage netto. Rechnungen bitte als PDF an rechnung@example.de unter Angabe der Auftragsnummer."
            />
          </Field>
        </div>
      </Card>

      <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={vorschauLaden}>
          Vorschau erzeugen
        </button>
        <button className="btn primary" disabled={busy === 'speichern'} onClick={speichern}>
          {busy === 'speichern' ? 'Wird gespeichert …' : 'Speichern und Vorschau'}
        </button>
      </div>

      <Card
        title="Musterdokument"
        subtitle="So sehen Auftragsbericht und Beauftragung mit den gespeicherten Einstellungen aus"
      >
        {vorschau ? (
          <object
            data={vorschau}
            type="application/pdf"
            style={{ width: '100%', height: 620, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
          >
            <p className="small">
              Die Vorschau lässt sich hier nicht anzeigen.{' '}
              <a href={vorschau} target="_blank" rel="noreferrer">
                Musterdokument öffnen
              </a>
            </p>
          </object>
        ) : (
          <div className="note-box tone-info">
            Noch keine Vorschau erzeugt. Die Vorschau zeigt die <em>gespeicherten</em> Einstellungen – Änderungen oben
            werden erst nach dem Speichern sichtbar.
          </div>
        )}
      </Card>
    </div>
  );
}
